import Component from '@glimmer/component';
import { array, hash } from '@ember/helper';
import { t } from 'ember-intl';
import { and, notEq } from 'ember-truth-helpers';
import {
  HdsPageHeader,
  HdsAlert,
  HdsTextBody,
  HdsButton,
  HdsTable,
  HdsIcon,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { boxPadding } from 'core/modifiers';
import {
  optionForResourceService,
  optionForSourceState,
  labelForRegion,
} from 'core/helpers';
import linkToTfcResource from 'core/helpers/link-to-tfc-resource';
import { supportTicketPrefillHref } from 'core/utils/support-ticket';
import LocationLink from 'hcp/components/location-link';
import { ACTIVE_RESOURCES_ORIGIN_PROJECT_DELETE } from 'hcp/utils/constants';

/**
 *
 * Displays the Project's active resources using a table.
 *
 *
 * ```
 * <Page::Projects::ActiveResources
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageProjectsActiveResources
 *
 */
export default class PageProjectsActiveResources extends Component {
  /**
   * A object contaning a pagination and resources key
   * @argument model
   * @type {Object}
   */

  get resources() {
    let { resources } = this.args.model;
    return resources.map((resource) => ({
      ...resource.link,
      geo: resource?.geo,
    }));
  }

  get transitionedFromProjectDelete() {
    const { activeResourcesListOrigin } = this.args.model;
    return activeResourcesListOrigin === ACTIVE_RESOURCES_ORIGIN_PROJECT_DELETE;
  }

  get supportTicketHref() {
    return supportTicketPrefillHref(this.userContext);
  }
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.Title>
            {{t "components.page.projects.active-resources.title"}}
          </PH.Title>
        </HdsPageHeader>
      </:header>
      <:alert>
        {{#if this.transitionedFromProjectDelete}}
          <HdsAlert
            @icon="terraform"
            @type="inline"
            @color="neutral"
            data-test-hcp-tfc-resources-alert
            as |A|
          >
            <A.Title>
              {{t
                "components.page.projects.active-resources.tfc-resources-alert.title"
              }}
            </A.Title>
            <A.Description>
              <Flex @alignItems="center" @gap="md" {{boxPadding "0 0 md 0"}}>
                <HdsTextBody>
                  {{t
                    "components.page.projects.active-resources.tfc-resources-alert.body"
                    htmlSafe=true
                    projectName=@model.project.name
                  }}
                </HdsTextBody>
              </Flex>
              <Flex
                @alignItems="center"
                @direction="row"
                @gap="md"
                data-test-hcp-project-delete-error-controls
              >
                <HdsButton
                  @color="secondary"
                  @icon="external-link"
                  @iconPosition="trailing"
                  @size="small"
                  @text={{t
                    "components.page.projects.active-resources.tfc-resources-alert.action"
                  }}
                  @href={{linkToTfcResource
                    undefined
                    "hashicorp.resource-manager.project"
                    @model.organization.name
                    @model.project.id
                  }}
                />
              </Flex>
            </A.Description>
          </HdsAlert>
        {{/if}}
      </:alert>
      <:body>
        {{#if this.resources.length}}
          <HdsTable
            @model={{this.resources}}
            @columns={{array
              (hash
                label=(t
                  "components.page.projects.active-resources.table.headers.resource"
                )
                width="300px"
              )
              (hash
                label=(t
                  "components.page.projects.active-resources.table.headers.type"
                )
              )
              (hash
                label=(t
                  "components.page.projects.active-resources.table.headers.service"
                )
              )
              (hash
                label=(t
                  "components.page.projects.active-resources.table.headers.provider"
                )
              )
              (hash
                label=(t
                  "components.page.projects.active-resources.table.headers.region"
                )
              )
            }}
            @valign="middle"
            data-test-active-resources-table
          >
            <:body as |B|>
              {{#let (optionForResourceService B.data) as |option|}}
                <B.Tr data-test-active-resources-table-row>
                  <B.Td
                    data-test-active-resources-table-cell
                    data-test-active-resources-table-cell-id
                  >
                    <LocationLink
                      @color="secondary"
                      @hideIcon={{true}}
                      @link={{B.data}}
                      class="hds-font-weight-medium"
                    />
                  </B.Td>
                  <B.Td
                    data-test-active-resources-table-cell
                    data-test-active-resources-table-cell-resource-type
                  >
                    {{#if option.type}}
                      {{option.type}}
                    {{else if B.data.description}}
                      {{B.data.description}}
                    {{else}}
                      {{B.data.type}}
                    {{/if}}
                  </B.Td>
                  <B.Td
                    data-test-active-resources-table-cell
                    data-test-active-resources-table-cell-service
                  >
                    <Flex @alignItems="center" @gap="xs">
                      <HdsIcon @name={{option.icon}} @isInline={{true}} />
                      <span>{{option.name}}</span>
                    </Flex>
                  </B.Td>
                  {{#let
                    (optionForSourceState
                      "region" B.data.location.region.provider
                    )
                    as |option|
                  }}
                    <B.Td
                      data-test-active-resources-table-cell
                      data-test-active-resources-table-cell-provider
                    >
                      {{#if option.isRegistered}}
                        <Flex @alignItems="center" @gap="2xs">
                          <HdsIcon @name={{option.icon}} @isInline={{true}} />
                          {{t option.text}}
                        </Flex>
                      {{else}}
                        —
                      {{/if}}
                    </B.Td>
                    <B.Td
                      data-test-active-resources-table-cell
                      data-test-active-resources-table-cell-region
                    >
                      {{#if
                        (and
                          (and
                            B.data.location.region.provider
                            B.data.location.region.region
                          )
                          (notEq B.data.location.region.provider "self-managed")
                        )
                      }}
                        <Flex @direction="row" @gap="2xs">
                          <span
                            data-test-active-resources-table-cell-region-display
                          >
                            {{t
                              (labelForRegion
                                B.data.location.region.region
                                provider=B.data.location.region.provider
                              )
                            }}
                          </span>
                          <span
                            data-test-active-resources-table-cell-region-name
                          >
                            ({{B.data.location.region.region}})
                          </span>
                        </Flex>
                      {{else}}
                        —
                      {{/if}}
                    </B.Td>
                  {{/let}}
                </B.Tr>
              {{/let}}
            </:body>
          </HdsTable>
        {{else}}
          <HdsApplicationState
            {{boxPadding "2xl 0 2xl 0"}}
            data-test-active-resources-zero-state
            as |A|
          >
            <A.Header
              @title={{t
                "components.page.projects.active-resources.no-active-resources"
              }}
            />
            <A.Body
              @text={{t
                "components.page.projects.active-resources.currently-no-resources-in-project"
              }}
            />
          </HdsApplicationState>
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
